// Projects Service for backend communication
import { api } from './api';

export type ProjectCategory = 'Opus' | 'Piece';

export interface ProjectDto {
  id: string;
  userId: string;
  title: string;
  description?: string;
  projectType: string;
  category: ProjectCategory;
  status: string;
  currentStage: string;
  workflowTemplateId?: string;
  targetCompletionDate?: string;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  // Hierarchical Projects
  parentProjectId?: string;
  isParentProject: boolean;
  isSubProject: boolean;
  subProjectCount: number;
  completedSubProjectCount: number;
}

export interface ProjectBoardDto {
  userId: string;
  totalProjects: number;
  activeProjects: number;
  archivedProjects: number;
  projectLimit: number;
  columns: BoardColumnDto[];
}

export interface BoardColumnDto {
  stageName: string;
  stageIndex: number;
  projects: ProjectCardDto[];
}

export interface ProjectCardDto {
  id: string;
  title: string;
  description?: string;
  projectType: string;
  category: ProjectCategory;
  currentStage: string;
  status: string;
  sessionCount: number;
  lastWorkedDate?: string;
  createdAt: string;
  // Hierarchical Projects
  parentProjectId?: string;
  isParentProject: boolean;
  isSubProject: boolean;
  subProjectCount: number;
  completedSubProjectCount: number;
}

export interface ProjectProgress {
  projectId: string;
  projectTitle: string;
  currentStage: string;
  currentStageIndex: number;
  totalStages: number;
  progressPercentage: number;
  nextStage?: string;
  isCompleted: boolean;
  stages: StageProgress[];
}

export interface StageProgress {
  index: number;
  name: string;
  isCompleted: boolean;
  isCurrent: boolean;
}

export interface CreateProjectRequest {
  title: string;
  description?: string;
  projectType: string;
  category?: ProjectCategory;
  initialStage: string;
  workflowTemplateId?: string;
  targetCompletionDate?: string;
}

export interface UpdateProjectRequest {
  title: string;
  description?: string;
  targetCompletionDate?: string;
  category?: ProjectCategory;
}

export interface CreateSubProjectRequest {
  title: string;
  description?: string;
  projectType: string;
  initialStage: string;
  workflowTemplateId?: string;
}

// Projects API
export const projectsApi = {
  // Get all projects for the current user
  getProjects: (status?: string): Promise<ProjectDto[]> => {
    const params = status ? `?status=${status}` : '';
    return api.get<ProjectDto[]>(`/projects${params}`);
  },

  // Get project by ID
  getProject: (id: string): Promise<ProjectDto> => {
    return api.get<ProjectDto>(`/projects/${id}`);
  },

  // Get project board (Kanban view)
  // - workflowTemplateId: filter by specific workflow template
  // - projectType: filter by project type (Music, Art, Writing)
  // - useUniversalStages: show all projects mapped to universal stages (Idea, In Progress, Review, Completed)
  // - showPiecesOnly: only show sub-projects (pieces), not parent projects (opus)
  getProjectBoard: (options?: {
    workflowTemplateId?: string;
    projectType?: string;
    useUniversalStages?: boolean;
    showPiecesOnly?: boolean;
  }): Promise<ProjectBoardDto> => {
    const params = new URLSearchParams();
    if (options?.workflowTemplateId) {
      params.append('workflowTemplateId', options.workflowTemplateId);
    }
    if (options?.projectType) {
      params.append('projectType', options.projectType);
    }
    if (options?.useUniversalStages) {
      params.append('useUniversalStages', 'true');
    }
    if (options?.showPiecesOnly) {
      params.append('showPiecesOnly', 'true');
    }
    const queryString = params.toString();
    const url = `/projects/board${queryString ? `?${queryString}` : ''}`;
    console.log('[ProjectsAPI] getProjectBoard options:', options, 'URL:', url);
    return api.get<ProjectBoardDto>(url);
  },

  // Get archived projects
  getArchivedProjects: (): Promise<ProjectDto[]> => {
    return api.get<ProjectDto[]>('/projects/archived');
  },

  // Get project progress
  getProjectProgress: (id: string): Promise<ProjectProgress> => {
    return api.get<ProjectProgress>(`/projects/${id}/progress`);
  },

  // Create a new project
  createProject: (data: CreateProjectRequest): Promise<ProjectDto> => {
    return api.post<ProjectDto>('/projects', data);
  },

  // Update a project
  updateProject: (id: string, data: UpdateProjectRequest): Promise<ProjectDto> => {
    return api.put<ProjectDto>(`/projects/${id}`, data);
  },

  // Change project stage (simple stage change)
  changeStage: (id: string, newStage: string): Promise<ProjectDto> => {
    return api.request<ProjectDto>(`/projects/${id}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ newStage }),
    });
  },

  // Move project to stage (with template validation)
  moveToStage: (id: string, newStage: string): Promise<ProjectDto> => {
    return api.put<ProjectDto>(`/projects/${id}/stage`, { newStage });
  },

  // Complete a project
  completeProject: (id: string): Promise<ProjectDto> => {
    return api.post<ProjectDto>(`/projects/${id}/complete`);
  },

  // Archive a project
  archiveProject: (id: string): Promise<ProjectDto> => {
    return api.post<ProjectDto>(`/projects/${id}/archive`);
  },

  // Unarchive a project
  unarchiveProject: (id: string): Promise<ProjectDto> => {
    return api.post<ProjectDto>(`/projects/${id}/unarchive`);
  },

  // Assign workflow template
  assignWorkflowTemplate: (id: string, workflowTemplateId?: string, resetToFirstStage = false): Promise<ProjectDto> => {
    return api.put<ProjectDto>(`/projects/${id}/workflow-template`, {
      workflowTemplateId,
      resetToFirstStage,
    });
  },

  // ==================== Sub-Project API ====================

  // Get sub-projects for a parent project
  getSubProjects: (parentProjectId: string): Promise<ProjectDto[]> => {
    return api.get<ProjectDto[]>(`/projects/${parentProjectId}/sub-projects`);
  },

  // Create a sub-project under a parent project
  createSubProject: (parentProjectId: string, data: CreateSubProjectRequest): Promise<ProjectDto> => {
    return api.post<ProjectDto>(`/projects/${parentProjectId}/sub-projects`, data);
  },
};

export default projectsApi;
